/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Pagination,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DarkUpperSection, LoadingSpinner, PageSection, StyledButton } from '../../../components';
import { useEffect, useState } from 'react';
import { LocationProps, UnknownLocation, LocationType } from '../../../datastore/locations';
import { useAlert, useCurrentUser, useDatastore, useIsOnline } from '../../../hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import AddLocationForm from './modals/AddLocationModal';
import EditLocationForm from './modals/EditLocationModal';
import OfflineCard from '../../../components/cards/OfflineCard';
import { useLocation } from 'react-router-dom';
import { adminActionType } from '../../../datastore/adminActionHistory';
import dayjs from 'dayjs';
import { captureError } from '../../../common/sentry';
import {
  QmSiteInboundLocationAdminOption,
  archiveQmSiteInboundLocationRequest,
  createQmSiteInboundSiteRequest,
  createQmSiteInboundLocationRequest,
  listQmSiteInboundLocationsAdminRequest,
  updateQmSiteInboundLocationRequest
} from '../../../api/qmCrud';

export default function ManageSubLocations() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const store = useDatastore();
  const { id } = useParams();
  const alert = useAlert();
  const user = useCurrentUser();
  const { isOnline } = useIsOnline();
  const [locations, setLocations] = useState<LocationProps[]>([]);
  const [selectedSubLocation, setSelectedSubLocation] = useState<LocationProps | null>(null);
  const [parentLocation, setParentLocation] = useState<LocationProps | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openSubEditModal, setOpenSubEditModal] = useState(false);
  const [openParentEditModal, setOpenParentEditModal] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [openAddLocation, setOpenAddLocation] = useState(false);
  const [qmSiteInboundLocations, setQmSiteInboundLocations] = useState<QmSiteInboundLocationAdminOption[]>([]);
  const [qmLocationDialogSite, setQmLocationDialogSite] = useState<LocationProps | null>(null);
  const [isQmLocationDialogLoading, setIsQmLocationDialogLoading] = useState(false);
  const [savingQmLocationCode, setSavingQmLocationCode] = useState<string | null>(null);
  const [qmLocationDraft, setQmLocationDraft] = useState({ code: '', label: '' });
  const [editingQmLocationCode, setEditingQmLocationCode] = useState<string | null>(null);
  const locationName = location.state?.name;
  const locationAlias = location.state?.alias;
  const locationAddress = location.state?.address;

  useEffect(() => {
    async function fetchLocations() {
      setIsLoading(true);
      try {
        if (!id) {
          alert('Error retrieving Sub Locations, please refresh your page', 'error');
          return;
        }

        const result = await store.locations.getById(id);
        if (!result) {
          alert('Error retrieving Locations, please refresh your page', 'error');
          return;
        }
        setParentLocation(result);
        const filterUnknown = result?.subLocations?.filter((location) => location.name !== UnknownLocation.Name);

        if (!filterUnknown) {
          alert('No locations found', 'warning');
          return;
        }

        setLocations(filterUnknown);
        try {
          const subLocationIds = filterUnknown.map((location) => location.id).filter(Boolean);
          const qmLocationResultGroups = await Promise.all(
            subLocationIds.map((siteId) => listQmSiteInboundLocationsAdminRequest(siteId))
          );
          setQmSiteInboundLocations(qmLocationResultGroups.flat());
        } catch (error) {
          console.error('Error fetching Site Inbound QM settings:', error);
          captureError(error, 'manageSubLocations-fetchQmSiteInboundSettings', {
            extra: { parentLocationId: id }
          });
          alert('Unable to load Site Inbound QM settings', 'warning');
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
        captureError(error, 'manageSubLocations-fetchLocations');
        alert('Error retrieving locations', 'error');
      }
      setIsLoading(false);
    }

    fetchLocations();
  }, [store.locations]);

  const qmSiteInboundLocationCountsBySite = qmSiteInboundLocations.reduce<Record<string, number>>((acc, qmLocation) => {
    if (!qmLocation.active) return acc;
    acc[qmLocation.receivingWarehouseId] = (acc[qmLocation.receivingWarehouseId] || 0) + 1;
    return acc;
  }, {});
  const activeQmSiteInboundLocationCount = locations.reduce(
    (count, location) => count + (qmSiteInboundLocationCountsBySite[location.id] || 0),
    0
  );
  const filteredLocations = locations.filter(
    (location) =>
      location.name?.toLowerCase().includes(filterText?.toLowerCase()) && (showDisabled || !location.isDisabled)
  );
  const dialogQmLocations = qmLocationDialogSite
    ? qmSiteInboundLocations
        .filter((location) => location.receivingWarehouseId === qmLocationDialogSite.id)
        .sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label))
    : [];

  const totalNumberOfPages = Math.ceil(filteredLocations.length / 25);
  const paginatedLocations = filteredLocations.slice((currentPage - 1) * 25, currentPage * 25);

  function handlePageChange(event: React.ChangeEvent<unknown>, value: number) {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleEditClick(location: LocationProps) {
    setSelectedSubLocation(location);
    setOpenSubEditModal(true);
  }

  async function syncLocationToQmSite(location: LocationProps, parentSiteId?: string | null) {
    await createQmSiteInboundSiteRequest({
      siteId: location.id,
      label: location.name,
      alias: location.alias || null,
      type: location.type || (parentSiteId ? LocationType.subLocation : LocationType.mainLocation),
      address: location.address || null,
      suburb: location.suburb || null,
      state: location.state || null,
      postcode: location.postcode || null,
      contactName: location.contactName || null,
      contactPhone: location.contactPhone || null,
      isAutoReceipt: Boolean(location.isAutoReceipt),
      deliveryDays: Number.isFinite(Number(location.deliveryDays)) ? Number(location.deliveryDays) : null,
      parentSiteId: parentSiteId || location.parentLocationId || null,
      active: !location.isDisabled
    });
  }

  async function syncParentAndSubLocationToQmSites(location: LocationProps) {
    if (parentLocation) {
      await syncLocationToQmSite(parentLocation, null);
    }
    await syncLocationToQmSite(location, id || location.parentLocationId || null);
  }

  async function refreshQmLocationsForSite(siteId?: string | null) {
    const qmLocationResults = await listQmSiteInboundLocationsAdminRequest(siteId || null);
    if (siteId) {
      setQmSiteInboundLocations((current) => [
        ...current.filter((location) => location.receivingWarehouseId !== siteId),
        ...qmLocationResults
      ]);
    } else {
      setQmSiteInboundLocations(qmLocationResults);
    }
    return qmLocationResults;
  }

  async function openQmLocationDialog(location: LocationProps) {
    if (!isOnline) {
      alert('Cannot configure QM locations while offline', 'warning');
      return;
    }
    setQmLocationDialogSite(location);
    setQmLocationDraft({ code: '', label: '' });
    setEditingQmLocationCode(null);
    setIsQmLocationDialogLoading(true);
    try {
      await syncParentAndSubLocationToQmSites(location);
      await refreshQmLocationsForSite(location.id);
    } catch (error) {
      console.log('Error opening Site Inbound QM location dialog:', error);
      captureError(error, 'manageSubLocations-openQmLocationDialog', {
        extra: { locationId: location.id, parentLocationId: id }
      });
      alert('Unable to load Site Inbound QM locations', 'error');
    } finally {
      setIsQmLocationDialogLoading(false);
    }
  }

  function closeQmLocationDialog() {
    setQmLocationDialogSite(null);
    setQmLocationDraft({ code: '', label: '' });
    setEditingQmLocationCode(null);
  }

  function startEditQmLocation(location: QmSiteInboundLocationAdminOption) {
    setEditingQmLocationCode(location.code);
    setQmLocationDraft({ code: location.code, label: location.label });
  }

  async function saveQmLocation() {
    const site = qmLocationDialogSite;
    if (!site) return;
    const code = qmLocationDraft.code.trim().toUpperCase();
    const label = qmLocationDraft.label.trim();
    if (!code || !label) {
      alert('Enter a code and label for the QM location', 'warning');
      return;
    }

    setSavingQmLocationCode(code);
    try {
      await syncParentAndSubLocationToQmSites(site);
      const existing = qmSiteInboundLocations.find(
        (location) => location.receivingWarehouseId === site.id && location.code === code
      );
      const displayOrder =
        existing?.displayOrder ??
        qmSiteInboundLocations.filter((location) => location.receivingWarehouseId === site.id).length + 1;
      const savedLocation =
        editingQmLocationCode || existing
          ? await updateQmSiteInboundLocationRequest({
              receivingWarehouseId: site.id,
              code,
              label,
              displayOrder
            })
          : await createQmSiteInboundLocationRequest({
              receivingWarehouseId: site.id,
              code,
              label,
              displayOrder
            });
      setQmSiteInboundLocations((current) => [
        ...current.filter(
          (location) => !(location.receivingWarehouseId === site.id && location.code === savedLocation.code)
        ),
        savedLocation
      ]);
      setQmLocationDraft({ code: '', label: '' });
      setEditingQmLocationCode(null);
      alert('Site Inbound QM location saved', 'success');
    } catch (error) {
      console.log('Error saving Site Inbound QM location:', error);
      captureError(error, 'manageSubLocations-saveQmLocation', {
        extra: { siteId: site.id, code }
      });
      alert('Error saving Site Inbound QM location', 'error');
    } finally {
      setSavingQmLocationCode(null);
    }
  }

  async function setQmLocationActive(location: QmSiteInboundLocationAdminOption, nextActive: boolean) {
    const site = qmLocationDialogSite;
    if (!site) return;
    setSavingQmLocationCode(location.code);
    try {
      const savedLocation = nextActive
        ? await createQmSiteInboundLocationRequest({
            receivingWarehouseId: site.id,
            code: location.code,
            label: location.label,
            displayOrder: location.displayOrder
          })
        : await archiveQmSiteInboundLocationRequest({
            receivingWarehouseId: site.id,
            code: location.code
          });
      setQmSiteInboundLocations((current) =>
        current.map((currentLocation) =>
          currentLocation.receivingWarehouseId === site.id && currentLocation.code === location.code
            ? savedLocation
            : currentLocation
        )
      );
    } catch (error) {
      console.log('Error updating Site Inbound QM location status:', error);
      captureError(error, 'manageSubLocations-setQmLocationActive', {
        extra: { siteId: site.id, code: location.code, nextActive }
      });
      alert('Error updating Site Inbound QM location', 'error');
    } finally {
      setSavingQmLocationCode(null);
    }
  }

  async function submitLocationEditForm(data: LocationProps) {
    try {
      if (data.name === UnknownLocation.Name) {
        alert('Cannot set a locations name to "Unknown"', 'warning');
        return;
      }
      if (data.type === LocationType.mainLocation) {
        data.deliveryDays = Number(data.deliveryDays);
      }
      setLocations(locations.map((loc) => (loc.id === data.id ? data : loc)));

      const updatedLocation = await store.locations.update(data);
      const originalLocation = locations.find((loc) => loc.id === data.id);
      if (data.type === LocationType.mainLocation) {
        await syncLocationToQmSite(updatedLocation ?? data, null);
      } else {
        await syncParentAndSubLocationToQmSites(updatedLocation ?? data);
      }

      await store.adminActionHistories.create({
        id: crypto.randomUUID(),
        actionType: adminActionType.LOCATION_UPDATE,
        beforeAction: originalLocation ? JSON.stringify(originalLocation) : 'Error writing before action',
        afterAction: updatedLocation ? JSON.stringify(updatedLocation) : 'Error writing after action',
        actionBy: user.email,
        actionTime: dayjs()
      });

      alert('Updated Location', 'success');
    } catch (error) {
      console.log('Error updating location:', error);
      captureError(error, 'manageSubLocations-submitLocationEditForm');
      alert('Error updating location', 'error');
    } finally {
      setOpenSubEditModal(false);
      if (data.type === LocationType.mainLocation) {
        setSelectedSubLocation(null);
        setOpenParentEditModal(false);
      }
    }
  }

  async function handleAddSubLocation(data: LocationProps) {
    try {
      if (data.name === UnknownLocation.Name) {
        alert('Cannot create location with name "Unknown"', 'warning');
        return;
      }
      if (!id) {
        alert('Error creating Sub Location, please refresh your page and try again', 'error');
        return;
      }
      setLocations([...locations, data]);

      data.id = crypto.randomUUID();
      data.type = LocationType.subLocation;
      data.parentLocationId = id;

      const createdLocation = await store.locations.create(data);
      if (createdLocation) {
        await syncParentAndSubLocationToQmSites(createdLocation);
      }

      await store.adminActionHistories.create({
        id: crypto.randomUUID(),
        actionType: adminActionType.LOCATION_CREATE,
        beforeAction: null,
        afterAction: createdLocation ? JSON.stringify(createdLocation) : 'Error writing create action',
        actionBy: user.email,
        actionTime: dayjs()
      });

      alert('Created Location', 'success');
    } catch (error) {
      console.log('Error creating location:', error);
      captureError(error, 'manageSubLocations-handleAddSubLocation');
      alert('Error creating location', 'error');
    } finally {
      setOpenAddLocation(false);
    }
  }

  if (!user.isAdmin) return null;

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={4} sx={{ padding: '1em 0' }}>
          <Stack direction="row" alignItems="center">
            <IconButton onClick={() => navigate('/settings/locations')}>
              <ArrowBackIcon fontSize="large" sx={{ color: theme.palette.primary.contrastText }} />
            </IconButton>
            <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
              Locations
            </Typography>
          </Stack>
          <Stack spacing={2} direction="row" justifyContent="space-between">
            <Stack>
              <Typography variant="h4" color={theme.palette.primary.contrastText}>
                {locationName} ({locationAlias})
              </Typography>
              <Typography sx={{ paddingTop: '0.5em' }} color={theme.palette.primary.contrastText}>
                {locationAddress}
              </Typography>
            </Stack>
            <Stack>
              <StyledButton variant="contained" onClick={() => setOpenParentEditModal(true)}>
                Edit Main Location
              </StyledButton>
            </Stack>
          </Stack>
        </Stack>
      </DarkUpperSection>
      {isOnline ? (
        <LoadingSpinner isLoading={isLoading}>
          <PageSection>
            <Stack spacing={4} sx={{ padding: '2em 0' }}>
              <Typography variant="body2" color="text.secondary">
                {activeQmSiteInboundLocationCount} active Site Inbound QM location
                {activeQmSiteInboundLocationCount === 1 ? '' : 's'}
              </Typography>
              <TextField
                label="Filter by Name"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                variant="outlined"
                sx={{ flexGrow: 1 }}
              />
              <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
                <FormControlLabel
                  control={<Switch checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} />}
                  label="Show Disabled Sites"
                />
                <StyledButton variant="contained" onClick={() => setOpenAddLocation(true)}>
                  Add Sub Location
                </StyledButton>
              </Stack>
            </Stack>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Name</b>
                  </TableCell>
                  <TableCell>
                    <b>Alias</b>
                  </TableCell>
                  <TableCell>
                    <b>Site Inbound QM Site Specific Locations</b>
                  </TableCell>
                  <TableCell>
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLocations.map((location) => (
                  <TableRow key={location.id}>
                    <TableCell>{location.name}</TableCell>
                    <TableCell>{location.alias}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">
                          {qmSiteInboundLocationCountsBySite[location.id] || 0} active
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={Boolean(parentLocation?.isDisabled || location.isDisabled)}
                          onClick={() => void openQmLocationDialog(location)}>
                          Manage
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Button onClick={() => handleEditClick(location)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Stack spacing={2} direction="row" justifyContent="center" sx={{ paddingTop: '1em', paddingBottom: '1em' }}>
              <Pagination count={totalNumberOfPages} page={currentPage} onChange={handlePageChange} color="primary" />
            </Stack>
          </PageSection>
        </LoadingSpinner>
      ) : (
        <PageSection>
          <Stack justifyContent="center">
            <OfflineCard />
          </Stack>
        </PageSection>
      )}
      <EditLocationForm
        open={openSubEditModal}
        onClose={() => setOpenSubEditModal(false)}
        editLocationType={LocationType.subLocation}
        location={selectedSubLocation}
        onSubmit={submitLocationEditForm}
      />
      <AddLocationForm
        open={openAddLocation}
        addLocationType={LocationType.subLocation}
        onClose={() => setOpenAddLocation(false)}
        onSubmit={handleAddSubLocation}
      />
      <EditLocationForm
        open={openParentEditModal}
        editLocationType={LocationType.mainLocation}
        onClose={() => setOpenParentEditModal(false)}
        location={parentLocation}
        onSubmit={submitLocationEditForm}
      />
      <Dialog open={Boolean(qmLocationDialogSite)} onClose={closeQmLocationDialog} fullWidth maxWidth="md">
        <DialogTitle>
          Site Inbound QM Site Specific Locations{qmLocationDialogSite ? ` - ${qmLocationDialogSite.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Code"
                value={qmLocationDraft.code}
                onChange={(event) =>
                  setQmLocationDraft((current) => ({ ...current, code: event.target.value.toUpperCase() }))
                }
                disabled={Boolean(editingQmLocationCode)}
                size="small"
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="Label"
                value={qmLocationDraft.label}
                onChange={(event) => setQmLocationDraft((current) => ({ ...current, label: event.target.value }))}
                size="small"
                fullWidth
              />
              <StyledButton
                variant="contained"
                onClick={() => void saveQmLocation()}
                disabled={isQmLocationDialogLoading || Boolean(savingQmLocationCode)}>
                {editingQmLocationCode ? 'Update' : 'Add'}
              </StyledButton>
              {editingQmLocationCode && (
                <StyledButton
                  variant="outlined"
                  onClick={() => {
                    setEditingQmLocationCode(null);
                    setQmLocationDraft({ code: '', label: '' });
                  }}>
                  Cancel
                </StyledButton>
              )}
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Code</b>
                  </TableCell>
                  <TableCell>
                    <b>Label</b>
                  </TableCell>
                  <TableCell>
                    <b>Status</b>
                  </TableCell>
                  <TableCell>
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dialogQmLocations.map((location) => (
                  <TableRow key={`${location.receivingWarehouseId}-${location.code}`}>
                    <TableCell>{location.code}</TableCell>
                    <TableCell>{location.label}</TableCell>
                    <TableCell>{location.active ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => startEditQmLocation(location)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          disabled={savingQmLocationCode === location.code}
                          onClick={() => void setQmLocationActive(location, !location.active)}>
                          {location.active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {dialogQmLocations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No Site Inbound QM Site Specific locations configured for this sub-site.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Stack>
        </DialogContent>
        <DialogActions>
          <StyledButton variant="outlined" onClick={closeQmLocationDialog}>
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
